import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Users, Trophy, Clock, BadgeCheck, Banknote, Zap, Star, Mail, Receipt, Calculator, Megaphone, KeyRound, Plus, Dices, AlertTriangle, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { NeonCard, Spinner, NeonPrice } from "../../components/Neon";
import { gbp } from "../../lib/format";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const Kpi = ({ icon: Icon, label, value, color = "cyan" }) => (
  <NeonCard variant="flow" innerClassName="p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{label}</span>
      <Icon className={`h-5 w-5 text-[var(--neon-${color})]`} />
    </div>
    <p className={`mt-2 font-display text-3xl glow-${color}`}>{value}</p>
  </NeonCard>
);

const Tile = ({ icon: Icon, label, onClick, color = "cyan", badge }) => (
  <button onClick={onClick} data-testid={`admin-tile-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="relative rounded-xl border border-white/10 bg-[var(--surface)] p-4 flex flex-col items-center justify-center gap-2 hover:border-[var(--neon-cyan)] hover:bg-white/[0.03] transition-colors min-h-[96px]">
    {badge != null && badge > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold bg-[var(--neon-pink)] text-black rounded-full px-2 py-0.5">{badge}</span>}
    <Icon className={`h-6 w-6 text-[var(--neon-${color})]`} />
    <span className="text-xs text-center text-white">{label}</span>
  </button>
);

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <Spinner />;
  if (!stats) return <p className="text-[var(--text-muted)]">Unable to load stats.</p>;

  const chartData = (stats.per_competition || []).map((c) => ({ name: c.title.slice(0, 14), revenue: c.revenue }));
  const lossAlerts = (stats.per_competition || []).filter((c) => c.status === "active" && (c.revenue || 0) <= 0).length;
  const winnerAlerts = (stats.per_competition || []).filter((c) => ["drawn", "complete", "closed"].includes((c.status || "").toLowerCase())).length;
  const corpTax = stats.net_profit > 0 ? stats.net_profit * 0.19 : 0;

  return (
    <div data-testid="admin-dashboard">
      {/* Alert tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile icon={BadgeCheck} label="ID Reviews" badge={stats.pending_verifications} color="pink" onClick={() => nav("/admin/verifications")} />
        <Tile icon={Banknote} label="Cash-outs" badge={stats.pending_cashouts} color="gold" onClick={() => nav("/admin/cashouts")} />
        <Tile icon={AlertTriangle} label="Loss Alerts" badge={lossAlerts} color="pink" onClick={() => nav("/admin/competitions")} />
        <Tile icon={Trophy} label="Winner Alerts" badge={winnerAlerts} color="green" onClick={() => nav("/admin/competitions")} />
      </div>

      {/* KPIs */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Gross Revenue" value={gbp(stats.gross_revenue)} color="green" />
        <Kpi icon={Banknote} label="Net Profit" value={gbp(stats.net_profit)} color={stats.net_profit >= 0 ? "green" : "pink"} />
        <Kpi icon={Star} label="Sub Revenue" value={gbp(stats.subscription_revenue)} color="cyan" />
        <Kpi icon={Users} label="Subscribers" value={stats.active_subscribers} color="pink" />
        <Kpi icon={Users} label="Total Users" value={stats.total_users} color="cyan" />
        <Kpi icon={Trophy} label="Active Comps" value={stats.active_competitions} color="green" />
        <Kpi icon={BadgeCheck} label="Pending KYC" value={stats.pending_verifications} color="pink" />
        <Kpi icon={Clock} label="Pending Cash-outs" value={stats.pending_cashouts} color="gold" />
      </div>

      {/* Quick actions (app parity) */}
      <p className="mt-7 font-display uppercase text-xl text-white">Quick Actions</p>
      <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Tile icon={Plus} label="New Competition" color="cyan" onClick={() => nav("/admin/competitions")} />
        <Tile icon={Dices} label="Launch Draw" color="gold" onClick={() => nav("/admin/competitions")} />
        <Tile icon={Zap} label="Instant Wins" color="cyan" onClick={() => nav("/admin/competitions")} />
        <Tile icon={Star} label="Members / Subs" color="pink" onClick={() => nav("/admin/subscriptions")} />
        <Tile icon={Mail} label="Postal Entry" color="cyan" onClick={() => nav("/admin/competitions")} />
        <Tile icon={Receipt} label="Expenses" color="cyan" onClick={() => nav("/admin/expenses")} />
        <Tile icon={Megaphone} label="AI Marketing" color="pink" onClick={() => nav("/admin/marketing")} />
        <Tile icon={Calculator} label="Tax Estimator" color="green" onClick={() => document.getElementById("tax-estimator")?.scrollIntoView({ behavior: "smooth" })} />
        <Tile icon={BadgeCheck} label="ID Reviews" color="pink" onClick={() => nav("/admin/verifications")} />
        <Tile icon={Banknote} label="Cash-outs" color="gold" onClick={() => nav("/admin/cashouts")} />
        <Tile icon={KeyRound} label="API Key Vault" color="cyan" onClick={() => nav("/admin/settings")} />
        <Tile icon={Sparkles} label="Discounts" color="green" onClick={() => nav("/admin/discounts")} />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        {/* Revenue chart */}
        <NeonCard variant="soft" innerClassName="p-5">
          <p className="font-display uppercase text-xl text-white">Revenue by Competition</p>
          <div className="mt-4" style={{ width: "100%", height: 288 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.08)" />
                <XAxis dataKey="name" stroke="#A7B0BE" fontSize={11} />
                <YAxis stroke="#A7B0BE" fontSize={11} />
                <Tooltip contentStyle={{ background: "#07070A", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,.04)" }} />
                <Bar dataKey="revenue" fill="#00E5FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </NeonCard>

        {/* Tax estimator */}
        <NeonCard variant="green" innerClassName="p-5" id="tax-estimator">
          <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-[var(--neon-green)]" /><p className="font-display uppercase text-xl text-white">Tax Estimator</p></div>
          <div className="mt-3 space-y-2 text-sm">
            {[["Gross revenue", stats.gross_revenue], ["VAT collected", stats.vat], ["Gateway fees", stats.gateway_fees], ["Instant-win payouts", stats.instant_win_payouts], ["Expenses", stats.expenses], ["Cashed out", stats.cashed_out]].map(([l, v]) => (
              <div key={l} className="flex justify-between border-b border-white/5 py-1.5"><span className="text-[var(--text-muted)]">{l}</span><span className="text-white">{gbp(v)}</span></div>
            ))}
            <div className="flex justify-between py-1.5"><span className="text-[var(--text-muted)]">Net profit</span><NeonPrice value={gbp(stats.net_profit)} color={stats.net_profit >= 0 ? "green" : "pink"} className="text-lg" /></div>
            <div className="flex justify-between py-1.5"><span className="text-[var(--text-muted)]">Est. Corporation Tax (19%)</span><span className="glow-pink">{gbp(corpTax)}</span></div>
            <div className="flex justify-between py-1.5 border-t border-white/10 pt-2"><span className="text-white font-display uppercase">Est. take-home</span><NeonPrice value={gbp(Math.max(0, stats.net_profit - corpTax))} color="green" className="text-xl" /></div>
          </div>
          <p className="mt-3 text-[11px] text-[var(--text-muted)]">Estimate only — confirm figures with your accountant. VAT shown is collected on sales; corporation tax estimated at the 19% small-profits rate.</p>
        </NeonCard>
      </div>
    </div>
  );
}
